import { BusinessObjectBase } from './base';
import { notAvailableSchema } from '@schemas/business-objects/not-available';
import type { AlternateStudent } from './alternate-student';
import type { SelectedStudent } from './selected-student';
import type { Term } from './term';
import type { TermTutor } from './term-tutor';
import type { CalendarDate, Id, Time } from '../base-types';

export interface NotAvailableData {
  readonly id: Id;
  readonly name?: string;
  readonly startDate?: CalendarDate;
  readonly startTime?: Time;
  readonly endDate?: CalendarDate;
  readonly endTime?: Time;
  readonly termId: Id;
  readonly term?: Term;
  readonly termTutorId: Id;
  readonly termTutor?: TermTutor;
  readonly selectedStudentId: Id;
  readonly selectedStudent?: SelectedStudent;
  readonly alternateStudentId: Id;
  readonly alternateStudent?: AlternateStudent;
}

export interface NotAvailable extends Readonly<NotAvailableData> {}
type NotAvailableCreateWithChildrenData = Partial<NotAvailableData>;

export class NotAvailable extends BusinessObjectBase {
  static readonly entityName = 'NotAvailable' as const;
  static readonly plural = 'not-availables';
  static schema = notAvailableSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<NotAvailable[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new NotAvailable(data));
  }

  static async findOne(filter?: any): Promise<NotAvailable | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new NotAvailable(data) : null;
  }

  static async findById(id: Id, filter?: any): Promise<NotAvailable> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new NotAvailable(data);
  }

  static async create(
    data: Omit<NotAvailableData, 'id'>,
  ): Promise<NotAvailable> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new NotAvailable(result);
  }

  static async createMany(
    data: Omit<NotAvailableData, 'id'>[],
  ): Promise<NotAvailable[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new NotAvailable(item));
  }

  static async createWithChildren(
    data: NotAvailableCreateWithChildrenData,
  ): Promise<NotAvailable> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new NotAvailable(result);
  }

  static async createManyWithChildren(
    data: NotAvailableCreateWithChildrenData[],
  ): Promise<NotAvailable[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new NotAvailable(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<NotAvailableData>,
  ): Promise<NotAvailable> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new NotAvailable(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
